import Label from '../../Utils/FormInputs/Label';
import TextField from '../../Utils/FormInputs/TextField';
import React, { useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import Button from '@material-ui/core/Button';
import Checkbox from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Chip from '@material-ui/core/Chip';
import Divider from '@material-ui/core/Divider';
import CircularProgress from '@material-ui/core/CircularProgress';
import { Link } from 'react-router-dom';
import PageTitle from '../../Utils/smsTitle';
import { SubHeader } from '../../../Containers/SubHeader';
import axios from 'axios';
import { config } from '../../../Helpers/env';
import { surgeryService } from '../../../services/surgeryService';
import { StatusBadge, PathwayBadge } from '../shared';
import ChecklistItem from '../shared/ChecklistItem';
import LinkAdmissionView from './LinkAdmissionView';
import PreAdmissionChecklist from './PreAdmissionChecklist';


// ─── Default checklist items ─────────────────────────────────────────────────
const DEFAULT_CHECKLIST = [
  { label: 'Insurance pre-authorisation obtained', checked: false },
  { label: 'Consent forms signed', checked: false },
  { label: 'Pre-operative assessment complete', checked: false },
  { label: 'Blood cross-match ordered', checked: false },
  { label: 'Fasting instructions given', checked: false },
  { label: 'Patient informed of date/time', checked: false },
];

// ─── Styles ──────────────────────────────────────────────────────────────────
const useStyles = makeStyles((theme) => ({
  root: {
    margin: 22,
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 400,
  },
  // ── Top bar ────────────────────────────
  topBar: {
    padding: '16px 24px',
    marginBottom: 16,
    backgroundColor: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: 12,
  },
  topBarLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    flexWrap: 'wrap',
  },
  patientName: {
    fontWeight: 700,
    fontSize: '1.1rem',
  },
  patientMeta: {
    color: '#666',
    fontSize: '0.85rem',
  },
  topBarDivider: {
    width: 1,
    height: 32,
    backgroundColor: '#e0e0e0',
    margin: '0 4px',
  },
  procedureText: {
    fontWeight: 600,
    fontSize: '0.95rem',
    color: '#333',
  },
  surgeonText: {
    color: '#666',
    fontSize: '0.85rem',
  },
  statusChip: {
    fontWeight: 600,
    fontSize: '0.75rem',
    height: 26,
    borderRadius: 13,
    color: '#fff',
  },
  // ── Form panel ─────────────────────────
  formPaper: {
    padding: 24,
    backgroundColor: '#fff',
  },
  sectionTitle: {
    fontWeight: 600,
    fontSize: '1rem',
    color: '#333',
    marginBottom: 12,
  },
  sectionSubtitle: {
    fontWeight: 600,
    fontSize: '0.9rem',
    color: '#555',
    marginTop: 20,
    marginBottom: 8,
  },
  readOnlyField: {
    fontSize: '0.9rem',
    color: '#333',
    padding: '10px 0',
  },
  specialReqRow: {
    marginTop: 8,
  },
  // ── Right panel ────────────────────────
  surgeryCard: {
    padding: 16,
    backgroundColor: '#fafafa',
    border: '1px solid #e0e0e0',
    borderRadius: 6,
    marginBottom: 16,
  },
  surgeryCardTitle: {
    fontWeight: 600,
    fontSize: '0.9rem',
    color: '#555',
    marginBottom: 10,
  },
  surgeryCardRow: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '3px 0',
  },
  surgeryCardLabel: {
    fontSize: '0.8rem',
    color: '#777',
  },
  surgeryCardValue: {
    fontSize: '0.8rem',
    fontWeight: 500,
    color: '#333',
  },
  viewLink: {
    display: 'inline-block',
    marginTop: 8,
    fontSize: '0.8rem',
    fontWeight: 500,
    color: '#1976D2',
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  // ── Bed grid ───────────────────────────
  bedSectionTitle: {
    fontWeight: 600,
    fontSize: '0.9rem',
    color: '#555',
    marginBottom: 8,
  },
  wardSelect: {
    marginBottom: 12,
  },
  bedGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: 8,
    marginBottom: 8,
  },
  bedBox: {
    height: 44,
    borderRadius: 4,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '0.72rem',
    fontWeight: 600,
    cursor: 'pointer',
    border: '2px solid transparent',
    transition: 'border-color 0.15s ease, transform 0.1s ease',
  },
  bedAvailable: {
    backgroundColor: '#C8E6C9',
    color: '#2E7D32',
    '&:hover': {
      transform: 'scale(1.05)',
      borderColor: '#4CAF50',
    },
  },
  bedOccupied: {
    backgroundColor: '#E0E0E0',
    color: '#999',
    cursor: 'not-allowed',
  },
  bedSelected: {
    borderColor: '#1976D2',
    backgroundColor: '#BBDEFB',
    color: '#0D47A1',
    transform: 'scale(1.05)',
  },
  selectedBedLabel: {
    fontSize: '0.8rem',
    color: '#1976D2',
    fontWeight: 500,
    marginTop: 4,
    minHeight: 20,
  },
  // ── Bottom bar ─────────────────────────
  bottomBar: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 12,
    marginTop: 20,
    padding: '16px 0',
  },
  cancelBtn: {
    textTransform: 'none',
    fontWeight: 500,
  },
  submitBtn: {
    textTransform: 'none',
    fontWeight: 600,
    backgroundColor: '#4CAF50',
    color: '#fff',
    '&:hover': {
      backgroundColor: '#43A047',
    },
    '&:disabled': {
      backgroundColor: '#C8E6C9',
      color: '#fff',
    },
  },
}));

// =============================================================================
// COMPONENT
// =============================================================================
const SurgeryAdmissionPage = ({ match, history }) => {
  const classes = useStyles();

  const surgeryId = match && match.params ? match.params.surgeryId : null;

  // ── State ──────────────────────────────
  const [surgery, setSurgery] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [isAlreadyAdmitted, setIsAlreadyAdmitted] = useState(false);
  const [admissionDetails, setAdmissionDetails] = useState(null);

  // Form data
  const [formData, setFormData] = useState({
    guardianName: '',
    relationship: '',
    guardianContact: '',
    guardianAddress: '',
    insuranceProvider: '',
    insuranceScheme: '',
    insuranceMemberNo: '',
    admissionNotes: '',
    expectedStay: '',
    specialRequirements: {
      isolation: false,
      icuStandby: false,
      bloodOnStandby: false,
      barrierNursing: false,
    },
  });

  // Bed assignment
  const [wards, setWards] = useState([]);
  const [allBeds, setAllBeds] = useState([]);
  const [selectedWard, setSelectedWard] = useState('');
  const [selectedBed, setSelectedBed] = useState(null);

  // Checklist
  const [checklistItems, setChecklistItems] = useState(
    DEFAULT_CHECKLIST.map((item) => ({ ...item }))
  );

  // ── Load surgery data ──────────────────
  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    surgeryService
      .viewSurgery({ surgery_id: surgeryId })
      .then((res) => {
        if (cancelled) return;
        const data = res.data;
        setSurgery(data);

        // Pre-fill admission notes
        setFormData((prev) => ({
          ...prev,
          admissionNotes: `Surgical admission for ${data.procedure || 'procedure'}`,
        }));

        // Check if already admitted (mock: check a flag on the data)
        if (data.admission_id || data.admitted) {
          setIsAlreadyAdmitted(true);
          setAdmissionDetails({
            ward: 'Surgical Ward A',
            bed: 'SW-A-12',
            date: '25 May 2026',
            doctor: data.lead_surgeon || 'Dr. Kizza Andrew',
          });
        }

        setLoading(false);
      })
      .catch(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [surgeryId]);

  // ── Load wards and beds ─────────────────
  useEffect(() => {
    Promise.all([
      axios.get(`${config.smsUrl}/cbedmanager/wards`),
      axios.get(`${config.smsUrl}/cbedmanager/beds`),
    ]).then(([wardsRes, bedsRes]) => {
      setWards(Array.isArray(wardsRes.data) ? wardsRes.data : []);
      setAllBeds(Array.isArray(bedsRes.data) ? bedsRes.data : []);
    }).catch(() => {});
  }, []);

  // ── Handlers ───────────────────────────
  const handleFieldChange = (field) => (e) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleSpecialReq = (field) => (e) => {
    setFormData((prev) => ({
      ...prev,
      specialRequirements: {
        ...prev.specialRequirements,
        [field]: e.target.checked,
      },
    }));
  };

  const handleChecklistChange = (index, checked) => {
    setChecklistItems((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], checked };
      return updated;
    });
  };

  const handleBedClick = (bed) => {
    if (!bed.available) return;
    setSelectedBed(bed.bed_id === selectedBed ? null : bed.bed_id);
  };

  const handleLinkConfirm = () => {
    setSubmitting(true);
    surgeryService
      .linkAdmission({ surgery_id: surgeryId, admission_id: admissionDetails?.admission_id })
      .then(() => {
        if (history) {
          history.push(`/surgery/detail/${surgeryId}`);
        }
      })
      .catch(() => {
        setSubmitting(false);
      });
  };

  const handleSubmit = () => {
    setSubmitting(true);
    const payload = {
      surgery_id: surgeryId,
      patient_number: surgery?.patient_number,
      guardian_name: formData.guardianName,
      relationship: formData.relationship,
      guardian_contact: formData.guardianContact,
      guardian_address: formData.guardianAddress,
      insurance_provider: formData.insuranceProvider,
      insurance_scheme: formData.insuranceScheme,
      insurance_member_no: formData.insuranceMemberNo,
      admission_notes: formData.admissionNotes,
      expected_stay_days: formData.expectedStay,
      special_requirements: formData.specialRequirements,
      bed_id: selectedBed,
      ward: selectedWard,
      checklist: checklistItems,
    };

    surgeryService
      .createSurgeryAdmission(payload)
      .then((res) => {
        const admissionId = res.data?.admission_id;
        return surgeryService.linkAdmission({
          surgery_id: surgeryId,
          admission_id: admissionId,
        });
      })
      .then(() => {
        if (history) {
          history.push(`/surgery/detail/${surgeryId}`);
        }
      })
      .catch(() => {
        setSubmitting(false);
      });
  };

  const handleCancel = () => {
    if (history) {
      history.goBack();
    }
  };

  // ── Loading state ──────────────────────
  if (loading) {
    return (
      <div>
        <PageTitle title="Surgery Admission" />
        <SubHeader title="Surgery" subTitle="Surgery Admission" />
        <div className={classes.loadingContainer}>
          <CircularProgress />
        </div>
      </div>
    );
  }

  if (!surgery) {
    return (
      <div>
        <PageTitle title="Surgery Admission" />
        <SubHeader title="Surgery" subTitle="Surgery Admission" />
        <Paper style={{ margin: 22, padding: 40, textAlign: 'center' }} elevation={1}>
          <Typography variant="h6" style={{ color: '#999' }}>
            Surgery not found
          </Typography>
        </Paper>
      </div>
    );
  }

  const bedGrid = selectedWard
    ? allBeds.filter((b) => b.ward_id === selectedWard)
    : [];

  // ── Already-admitted mode: show LinkAdmissionView ──
  if (isAlreadyAdmitted) {
    return (
      <div>
        <PageTitle title="Surgery Admission" />
        <SubHeader title="Surgery" subTitle="Link Admission" />
        <div className={classes.root}>
          <LinkAdmissionView
            surgery={surgery}
            admissionDetails={admissionDetails}
            onConfirm={handleLinkConfirm}
            onCancel={handleCancel}
          />
        </div>
      </div>
    );
  }

  // ── Full admission form ────────────────
  return (
    <div>
      <PageTitle title="Surgery Admission" />
      <SubHeader title="Surgery" subTitle="Surgery Admission" />

      <div className={classes.root}>
        {/* ═══════════════ TOP BAR ═══════════════ */}
        <Paper className={classes.topBar} elevation={1}>
          <div className={classes.topBarLeft}>
            <div>
              <Typography className={classes.patientName}>
                {surgery.patient_name}
              </Typography>
              <Typography className={classes.patientMeta}>
                {surgery.patient_number} &middot; Age {surgery.age || 45} &middot;{' '}
                {surgery.gender || 'Male'}
              </Typography>
            </div>
            <div className={classes.topBarDivider} />
            <div>
              <Typography className={classes.procedureText}>
                {surgery.procedure}
              </Typography>
              <Typography className={classes.surgeonText}>
                {surgery.scheduled_date} &middot; {surgery.lead_surgeon}
              </Typography>
            </div>
          </div>
          <Chip
            label="Awaiting Admission"
            className={classes.statusChip}
            style={{ backgroundColor: '#FF9800' }}
            size="small"
          />
        </Paper>

        {/* ═══════════════ MAIN CONTENT ═══════════════ */}
        <Grid container spacing={2}>
          {/* ── Left Panel (60%) ─────────────────── */}
          <Grid item xs={12} md={7}>
            <Paper className={classes.formPaper} elevation={1}>
              <Typography className={classes.sectionTitle}>
                Admission Details
              </Typography>
              <Divider style={{ marginBottom: 16 }} />

              {/* Guardian Information */}
              <Typography className={classes.sectionSubtitle}>
                Guardian / Next of Kin
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Guardian Name"
                    value={formData.guardianName}
                    onChange={handleFieldChange('guardianName')}
                    fullWidth
                    size="small"
                    variant="outlined"
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Select
                    value={formData.relationship}
                    onChange={handleFieldChange('relationship')}
                    displayEmpty
                    fullWidth
                    variant="outlined"
                    style={{ height: 40 }}
                  >
                    <MenuItem value="" disabled>
                      Relationship
                    </MenuItem>
                    <MenuItem value="Parent">Parent</MenuItem>
                    <MenuItem value="Spouse">Spouse</MenuItem>
                    <MenuItem value="Child">Child</MenuItem>
                    <MenuItem value="Sibling">Sibling</MenuItem>
                    <MenuItem value="Other">Other</MenuItem>
                  </Select>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Guardian Contact"
                    value={formData.guardianContact}
                    onChange={handleFieldChange('guardianContact')}
                    fullWidth
                    size="small"
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Guardian Address"
                    value={formData.guardianAddress}
                    onChange={handleFieldChange('guardianAddress')}
                    fullWidth
                    size="small"
                    variant="outlined"
                  />
                </Grid>
              </Grid>

              {/* Insurance */}
              <Typography className={classes.sectionSubtitle}>
                Insurance Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Provider"
                    value={formData.insuranceProvider}
                    onChange={handleFieldChange('insuranceProvider')}
                    fullWidth
                    size="small"
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Scheme"
                    value={formData.insuranceScheme}
                    onChange={handleFieldChange('insuranceScheme')}
                    fullWidth
                    size="small"
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Member #"
                    value={formData.insuranceMemberNo}
                    onChange={handleFieldChange('insuranceMemberNo')}
                    fullWidth
                    size="small"
                    variant="outlined"
                  />
                </Grid>
              </Grid>

              {/* Admission Notes */}
              <Typography className={classes.sectionSubtitle}>
                Admission Notes
              </Typography>
              <TextField
                value={formData.admissionNotes}
                onChange={handleFieldChange('admissionNotes')}
                fullWidth
                size="small"
                variant="outlined"
                multiline
                rows={3}
              />

              {/* Admission Reason (read-only) */}
              <Typography className={classes.sectionSubtitle}>
                Admission Reason
              </Typography>
              <Typography className={classes.readOnlyField}>
                Surgical Admission &mdash; {surgery.procedure}
              </Typography>

              {/* Expected Stay */}
              <Grid container spacing={2} style={{ marginTop: 4 }}>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Expected Stay (days)"
                    value={formData.expectedStay}
                    onChange={handleFieldChange('expectedStay')}
                    fullWidth
                    size="small"
                    variant="outlined"
                    type="number"
                    inputProps={{ min: 1 }}
                  />
                </Grid>
              </Grid>

              {/* Special Requirements */}
              <Typography className={classes.sectionSubtitle}>
                Special Requirements
              </Typography>
              <div className={classes.specialReqRow}>
                <Grid container>
                  <Grid item xs={6} sm={3}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.specialRequirements.isolation}
                          onChange={handleSpecialReq('isolation')}
                          color="primary"
                          size="small"
                        />
                      }
                      label={<Typography variant="body2">Isolation</Typography>}
                    />
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.specialRequirements.icuStandby}
                          onChange={handleSpecialReq('icuStandby')}
                          color="primary"
                          size="small"
                        />
                      }
                      label={<Typography variant="body2">ICU standby</Typography>}
                    />
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.specialRequirements.bloodOnStandby}
                          onChange={handleSpecialReq('bloodOnStandby')}
                          color="primary"
                          size="small"
                        />
                      }
                      label={
                        <Typography variant="body2">Blood on standby</Typography>
                      }
                    />
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.specialRequirements.barrierNursing}
                          onChange={handleSpecialReq('barrierNursing')}
                          color="primary"
                          size="small"
                        />
                      }
                      label={
                        <Typography variant="body2">Barrier nursing</Typography>
                      }
                    />
                  </Grid>
                </Grid>
              </div>
            </Paper>
          </Grid>

          {/* ── Right Panel (40%) ────────────────── */}
          <Grid item xs={12} md={5}>
            {/* Surgery Details Card */}
            <div className={classes.surgeryCard}>
              <Typography className={classes.surgeryCardTitle}>
                Surgery Details
              </Typography>
              <div className={classes.surgeryCardRow}>
                <Typography className={classes.surgeryCardLabel}>Procedure</Typography>
                <Typography className={classes.surgeryCardValue}>
                  {surgery.procedure}
                </Typography>
              </div>
              <div className={classes.surgeryCardRow}>
                <Typography className={classes.surgeryCardLabel}>Date</Typography>
                <Typography className={classes.surgeryCardValue}>
                  {surgery.scheduled_date}
                </Typography>
              </div>
              <div className={classes.surgeryCardRow}>
                <Typography className={classes.surgeryCardLabel}>Time</Typography>
                <Typography className={classes.surgeryCardValue}>
                  {surgery.estimated_start_time || '08:00'}
                </Typography>
              </div>
              <div className={classes.surgeryCardRow}>
                <Typography className={classes.surgeryCardLabel}>Theatre</Typography>
                <Typography className={classes.surgeryCardValue}>
                  {surgery.theatre || 'Theatre A'}
                </Typography>
              </div>
              <div className={classes.surgeryCardRow}>
                <Typography className={classes.surgeryCardLabel}>Surgeon</Typography>
                <Typography className={classes.surgeryCardValue}>
                  {surgery.lead_surgeon}
                </Typography>
              </div>
              <div className={classes.surgeryCardRow}>
                <Typography className={classes.surgeryCardLabel}>Pre-Op Status</Typography>
                <Typography className={classes.surgeryCardValue}>
                  {surgery.pre_op?.assessment_done ? 'Complete' : 'Pending'}
                </Typography>
              </div>
              <Link
                to={`/surgery/detail/${surgeryId}`}
                className={classes.viewLink}
              >
                View Surgery Details
              </Link>
            </div>

            {/* Bed Assignment */}
            <Paper style={{ padding: 16, marginBottom: 16 }} elevation={1}>
              <Typography className={classes.bedSectionTitle}>
                Bed Assignment
              </Typography>
              <Select
                value={selectedWard}
                onChange={(e) => {
                  setSelectedWard(e.target.value);
                  setSelectedBed(null);
                }}
                fullWidth
                variant="outlined"
                className={classes.wardSelect}
                style={{ height: 40 }}
              >
                {wards.map((ward) => (
                  <MenuItem key={ward.ward_id} value={ward.ward_id}>
                    {ward.name}
                  </MenuItem>
                ))}
              </Select>

              <div className={classes.bedGrid}>
                {bedGrid.length === 0 ? (
                  <Typography variant="body2" style={{ color: '#999', fontStyle: 'italic', padding: 8 }}>
                    {selectedWard ? 'No beds found for this ward' : 'Select a ward to see beds'}
                  </Typography>
                ) : (
                  bedGrid.map((bed) => {
                    const isAvailable = bed.status === 0;
                    let boxClass = `${classes.bedBox} `;
                    if (selectedBed === bed.bed_id) {
                      boxClass += classes.bedSelected;
                    } else if (isAvailable) {
                      boxClass += classes.bedAvailable;
                    } else {
                      boxClass += classes.bedOccupied;
                    }
                    return (
                      <div
                        key={bed.bed_id}
                        className={boxClass}
                        onClick={() => isAvailable && handleBedClick(bed)}
                        style={{ cursor: isAvailable ? 'pointer' : 'not-allowed' }}
                      >
                        {bed.bed_number}
                      </div>
                    );
                  })
                )}
              </div>
              <Typography className={classes.selectedBedLabel}>
                {selectedBed
                  ? `Selected: Bed ${selectedBed}`
                  : 'Click an available bed to assign'}
              </Typography>
            </Paper>

            {/* Pre-Admission Checklist */}
            <PreAdmissionChecklist
              items={checklistItems}
              onChange={handleChecklistChange}
              disabled={submitting}
            />
          </Grid>
        </Grid>

        {/* ═══════════════ BOTTOM BAR ═══════════════ */}
        <div className={classes.bottomBar}>
          <Button
            variant="outlined"
            className={classes.cancelBtn}
            onClick={handleCancel}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            className={classes.submitBtn}
            onClick={handleSubmit}
            disabled={submitting || !selectedBed || !formData.guardianName}
          >
            {submitting ? (
              <CircularProgress size={20} style={{ color: '#fff' }} />
            ) : (
              'Admit Patient & Assign Bed'
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SurgeryAdmissionPage;
